function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;
  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { FormProvider } from "../hooks/internal/useForm";
import { getPath, setPath, deletePath } from "../utils/objectPath";
import { responsiveWidthType } from "../hooks/useResponsiveProp";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { responsiveSize } from "../utils/css";
import { formatArray } from "../utils/array";
import { jsx as ___EmotionJSX } from "@emotion/core";
var DEFAULT_PROPS = {
  debug: false
};
function getParentFieldName(target) {
  var _target$dataset$paren;
  return (_target$dataset$paren = target.dataset.parentName) !== null && _target$dataset$paren !== void 0 ? _target$dataset$paren : target.name;
}
Form.DEFAULT_PROPS = DEFAULT_PROPS;
function Form(_props) {
  var props = _objectSpread(_objectSpread({}, DEFAULT_PROPS), _props);
  var initialValues = props.initialValues,
    onSubmit = props.onSubmit,
    debug = props.debug,
    children = props.children,
    testId = props.testId;
  var _useState = useState({
      values: initialValues,
      errors: {},
      shouldValidateOnChange: false,
      namesToValidate: null,
      submitStatus: "READY"
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  var exposedState = useMemo(function () {
    return {
      values: state.values,
      errors: state.errors
    };
  }, [state.values, state.errors]);
  var fields = useRef({});
  var lastFocusedFieldName = useRef(null);
  var lastMouseDownInputElement = useRef(null);
  var onFocus = function onFocus(event) {
    var target = event.target;
    var parentName = getParentFieldName(target);
    lastFocusedFieldName.current = parentName;
    setState(function (state) {
      var errors = getPath(state.errors, parentName);
      var hasErrors = Array.isArray(errors) && errors.length > 0;
      return setPath(state, "shouldValidateOnChange", hasErrors);
    });
  };
  var onBlur = function onBlur(event) {
    var target = event.target;
    var parentName = getParentFieldName(target);
    lastFocusedFieldName.current = null;
    if (target === lastMouseDownInputElement.current) {
      lastMouseDownInputElement.current = null;
    }
    /* 
      We use setTimeout in order to differentiate between onBlur to another field within
      the same parent (e.g. DatePicker) and onBlur out of the parent.
    */

    setTimeout(function () {
      if (parentName !== lastFocusedFieldName.current && (lastMouseDownInputElement.current === null || parentName !== getParentFieldName(lastMouseDownInputElement.current))) {
        setState(function (state) {
          return setPath(state, "namesToValidate", [parentName]);
        });
      }
    });
  };
  var onChange = function onChange(event) {
    var _event$value;
    var target = event.target;
    var name = target.name;
    var isCheckbox = target.type === "checkbox";
    var newValue = isCheckbox ? target.checked : (_event$value = event.value) !== null && _event$value !== void 0 ? _event$value : target.value;
    setState(function (state) {
      var newState = setPath(state, "values.".concat(name), newValue);
      /*
        Without validating the Checkbox on every change, we have the following unwanted behaviour.
        When the Checkbox is not checked:
          1. Click the Checkbox twice (it should be unchecked again).
          2. Press the Checkbox without releasing it (validation error appears).
          3. If you resease the Checkbox now, the validation error disappears.
      */

      if (state.shouldValidateOnChange || isCheckbox) {
        newState = setPath(newState, "namesToValidate", [getParentFieldName(target)]);
      }
      return newState;
    });
  };
  var onMouseDown = function onMouseDown(event) {
    var target = event.target;
    var document = target.ownerDocument;
    var inputElement = document.querySelector("#".concat(target.htmlFor));
    lastMouseDownInputElement.current = inputElement;
  };
  var registerField = function registerField(name, field) {
    fields.current[name] = field;
  };
  var unregisterField = function unregisterField(name) {
    delete fields.current[name];
  };
  var providerValue = {
    state: state,
    onFocus: onFocus,
    // should be called by inputs
    onBlur: onBlur,
    // should be called by inputs
    onChange: onChange,
    // should be called by inputs
    onMouseDown: onMouseDown,
    // should be called by input labels that have a "for" attribute
    registerField: registerField,
    unregisterField: unregisterField
  };
  var getFieldErrors = useCallback(function (values, name) {
    var _field$data;
    var value = getPath(values, name);
    var field = fields.current[name];
    if (field.disabled === true || field.optional === true && typeof ((_field$data = field.data) === null || _field$data === void 0 ? void 0 : _field$data.isEmpty) === "function" && field.data.isEmpty(value) === true) {
      return null;
    }
    if (typeof field.validate === "function") {
      var errors = field.validate(value, field.data);
      if (typeof errors === "string") {
        return [errors];
      }
      if (Array.isArray(errors) && errors.length > 0) {
        return errors;
      }
    }
    return null;
  }, []);
  var getNewErrors = useCallback(function (state) {
    var namesToValidate = state.namesToValidate,
      values = state.values;
    return namesToValidate.reduce(function (acc, name) {
      var errors = getFieldErrors(values, name);
      return errors === null ? deletePath(acc, name, {
        deleteEmptyObjects: true
      }) : setPath(acc, name, errors);
    }, state.errors);
  }, [getFieldErrors]);
  var validateField = useCallback(function (name) {
    if (fields.current[name]) {
      setState(function (state) {
        var errors = getFieldErrors(state.values, name);
        return errors === null ? deletePath(state, "errors.".concat(name), {
          deleteEmptyObjects: {
            except: ["errors"]
          }
        }) : setPath(state, "errors.".concat(name), errors);
      });
    } else {
      console.error("Can't validate field \"".concat(name, "\" because it's not found. Existing fields: ").concat(formatArray(Object.keys(fields.current))));
    }
  }, [getFieldErrors]);
  var submitForm = useCallback(function () {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        namesToValidate: Object.keys(fields.current),
        submitStatus: "VALIDATE_THEN_SUBMIT"
      });
    });
  }, []);
  var onFormSubmit = useCallback(function (event) {
    event.preventDefault();
    submitForm();
  }, [submitForm]);
  var setErrors = useCallback(function (errorsMap) {
    setState(function (state) {
      var newErrors = Object.keys(fields.current).reduce(function (acc, name) {
        if (typeof errorsMap[name] === "string") {
          acc[name] = [errorsMap[name]];
        } else if (Array.isArray(errorsMap[name])) {
          acc[name] = errorsMap[name];
        } else {
          acc[name] = state.errors[name];
        }
        return acc;
      }, {});
      return _objectSpread(_objectSpread({}, state), {}, {
        errors: newErrors
      });
    });
  }, []);
  var responsiveFormCSS = useResponsivePropsCSS(props, DEFAULT_PROPS, {
    width: responsiveSize("width")
  });
  useEffect(function () {
    if (state.namesToValidate === null) {
      return;
    }
    setState(function (state) {
      var newState = setPath(state, "errors", getNewErrors(state));
      if (state.submitStatus === "VALIDATE_THEN_SUBMIT") {
        newState = setPath(newState, "submitStatus", "SUBMIT");
      }
      return newState;
    });
  }, [state.namesToValidate, getNewErrors]);
  useEffect(function () {
    if (state.submitStatus === "SUBMIT") {
      onSubmit && onSubmit({
        errors: state.errors,
        values: state.values,
        setErrors: setErrors
      });
      setState(function (state) {
        return setPath(state, "submitStatus", "READY");
      });
    }
  }, [state.submitStatus, state.errors, state.values, onSubmit, setErrors]);
  return ___EmotionJSX(FormProvider, {
    value: providerValue
  }, ___EmotionJSX("form", {
    css: responsiveFormCSS,
    method: "POST",
    action: "#" // https://stackoverflow.com/a/45705325/247243
    ,

    noValidate: true,
    onSubmit: onFormSubmit,
    "data-testid": testId
  }, typeof children === "function" ? children({
    state: exposedState,
    validateField: validateField,
    submitForm: submitForm
  }) : children), debug && ___EmotionJSX("pre", null, JSON.stringify(exposedState, null, 2)));
}
Form.propTypes = _objectSpread(_objectSpread({}, responsiveWidthType), {}, {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  debug: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  testId: PropTypes.string
});
export default Form;